import React, { useState, useEffect } from "react";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";

const AdminLoginPage: React.FC = () => {
  const { login, profile, logout, user } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Only redirect if both user and profile exist and role is admin
  useEffect(() => {
    if (user && profile?.role === "admin") {
      navigate("/admin/platform");
    }
  }, [user, profile, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      // Login will trigger the useEffect above if successful
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // If logged in as non-admin, show error and logout option
  if (user && profile && profile.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Platform Admin Login</h1>
          <p className="mb-4 text-red-600">
            You are logged in as a {profile.role} user. Please log out and sign in with a platform admin account.
          </p>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={logout}
          >
            Log Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 rounded-xl shadow-md w-full max-w-md"
      >
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">TyreLink Platform</h1>
          <p className="text-gray-600 mt-1">Admin Access</p>
        </div>
        
        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-700">Email</label>
          <input
            type="email"
            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoFocus
            placeholder="Email"
          />
        </div>
        
        <div className="mb-6">
          <label className="block mb-1 font-medium text-gray-700">Password</label>
          <input
            type="password"
            className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            placeholder="Enter password"
          />
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? "Signing in..." : "Sign In to Platform Admin"}
        </button>

        <div className="flex justify-end mt-2">
          <button
            type="button"
            className="text-blue-600 text-sm hover:underline focus:outline-none"
            onClick={async () => {
              const adminEmail = prompt("Enter your admin email address to reset password:");
              if (adminEmail) {
                try {
                  setLoading(true);
                  setError(null);
                  // Supabase password reset
                  const { error } = await supabase.auth.resetPasswordForEmail(adminEmail, {
                    redirectTo: window.location.origin + "/admin/reset-password"
                  });
                  if (error) throw error;
                  alert("Password reset link sent to your email address.");
                } catch (err: any) {
                  setError(err.message || "Failed to send reset email.");
                } finally {
                  setLoading(false);
                }
              }
            }}
          >
            Forgot password?
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminLoginPage;